import { MutationCache } from '@tanstack/react-query';
import { useEffect, useSyncExternalStore } from 'react';

/**
 * La pantalla «Procesando…» global, montada una vez en App. Se usa así:
 *   await conProcesando(() => api.post(...), 'Generando el PDF…')
 *   useProcesando(mutacion.isPending, 'Enviando al SRI…')
 *   const listo = mostrarProcesando('Paso 1…'); cambiarPaso('Paso 2…'); listo()
 *
 * Y sin hacer nada: toda mutación que tarde más de 350 ms la abre sola
 * (ver `crearCacheDeMutacionesConEspera`, abajo).
 */
type Estado = { activas: number; paso: string | null };

let estado: Estado = { activas: 0, paso: null };
const oyentes = new Set<() => void>();

function emitir(siguiente: Estado) {
  estado = siguiente;
  oyentes.forEach((oyente) => oyente());
}

function suscribirse(oyente: () => void) {
  oyentes.add(oyente);
  return () => {
    oyentes.delete(oyente);
  };
}

const leerEstado = () => estado;

/** Abre la pantalla; devuelve la función que la cierra. Varias a la vez se suman. */
export function mostrarProcesando(paso?: string): () => void {
  emitir({ activas: estado.activas + 1, paso: paso ?? estado.paso });
  let cerrada = false;
  return () => {
    if (cerrada) {
      return;
    }
    cerrada = true;
    const activas = Math.max(0, estado.activas - 1);
    emitir({ activas, paso: activas ? estado.paso : null });
  };
}

/** Cambia el texto del paso en curso, si la pantalla está abierta. */
export function cambiarPaso(paso: string) {
  if (estado.activas) {
    emitir({ ...estado, paso });
  }
}

/** Ejecuta una tarea con la pantalla abierta, y la cierra pase lo que pase. */
export async function conProcesando<T>(tarea: () => Promise<T>, paso?: string): Promise<T> {
  const listo = mostrarProcesando(paso);
  try {
    return await tarea();
  } finally {
    listo();
  }
}

/** Mantiene la pantalla abierta mientras `activo` sea verdadero. */
export function useProcesando(activo: boolean, paso?: string) {
  useEffect(() => {
    if (!activo) {
      return;
    }
    return mostrarProcesando(paso);
  }, [activo, paso]);
}

export function useEstadoDeProceso() {
  return useSyncExternalStore(suscribirse, leerEstado);
}

/**
 * La espera automática: cualquier mutación —guardar, cobrar, emitir, subir— que tarde más de
 * `retardo` ms abre la pantalla. Las rápidas no parpadean. Por mutación, en `meta`:
 *   meta: { procesando: 'Enviando al SRI…' }   el texto del paso
 *   meta: { silenciosa: true }                   sin pantalla
 */
export function crearCacheDeMutacionesConEspera(retardo = 350) {
  type Pendiente = { temporizador: ReturnType<typeof setTimeout>; listo?: () => void };
  const pendientes = new WeakMap<object, Pendiente>();

  return new MutationCache({
    onMutate: (_variables, mutacion) => {
      if (mutacion.meta?.silenciosa) {
        return;
      }
      const paso =
        typeof mutacion.meta?.procesando === 'string' ? mutacion.meta.procesando : undefined;
      const pendiente: Pendiente = {
        temporizador: setTimeout(() => {
          pendiente.listo = mostrarProcesando(paso);
        }, retardo),
      };
      pendientes.set(mutacion, pendiente);
    },
    onSettled: (_datos, _error, _variables, _contexto, mutacion) => {
      const pendiente = pendientes.get(mutacion);
      if (!pendiente) {
        return;
      }
      clearTimeout(pendiente.temporizador);
      pendiente.listo?.();
      pendientes.delete(mutacion);
    },
  });
}
